// SketchColorPicker.jsx
// Color picker that uses the SketchPicker from react-colorful.

// Copyright HS Analysis GmbH, 2024
// Author: Viktor Eberhardt

// Framework imports
import React, { useState } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";

// External imports
import { makeStyles } from "@mui/styles";
import { HexAlphaColorPicker } from "react-colorful";

// Mui imports
import { presetColors } from "../utils/RandomColorGenerator";

/**
 * Color picker that uses the SketchPicker from react-colorful.
 * @param {string} color The color to display as a hex string.
 * @param {function} handleChange Callback function that receives the new color as a hex string.
 * @param {function} onClose Callback function that is called when the color picker is closed.
 * @param {bool} includeWhite Flag if the color white should be included in the color swatches.
 * @param {bool} interactive Flag if the color picker can be changed interactively.
 * @returns
 */
const SketchColorPicker = (props) => {
  const {
    color,
    handleChange,
    onClose,
    includeWhite,
    interactive = true,
  } = props;
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [xPosition, setXPosition] = useState(0);
  const [yPosition, setYPosition] = useState(0);

  let colorsForSwatches = presetColors;
  if (includeWhite) {
    colorsForSwatches = ["#ffffff", ...presetColors];
  }

  const handleClick = (e) => {
    let x = e.pageX;
    let y = e.pageY;

    const pickerWidth = 220; // approximate width of the color picker
    const pickerHeight = 300; // approximate height of the color picker

    if (y + pickerHeight > window.innerHeight) {
      y = window.innerHeight - pickerHeight - 10; // 10px margin
    }

    if (x + pickerWidth > window.innerWidth) {
      x = window.innerWidth - pickerWidth - 10; // 10px margin
    }

    setXPosition(x);
    setYPosition(y);
    setDisplayColorPicker(true && interactive);
    e.stopPropagation();
  };

  const handleClose = (e) => {
    setDisplayColorPicker(false);
    e.stopPropagation();
    onClose();
  };

  const classes = useStyles();

  return (
    <div className={classes.popover}>
      <div className={classes.mainSwatch} onClick={handleClick}>
        <div className={classes.color} style={{ background: color }} />
      </div>
      {displayColorPicker &&
        ReactDOM.createPortal(
          [
            <div
              key={"close"}
              className={classes.cover}
              onClick={handleClose}
            />,
            <div
              key={"picker"}
              className={classes.pickerContainer}
              style={{ top: yPosition - 25, left: xPosition - 10 }}
            >
              <HexAlphaColorPicker
                className="color-picker"
                color={color}
                onChange={handleChange}
              />
              <div className={classes.pickerSwatches}>
                {colorsForSwatches.map((presetColor) => (
                  <button
                    key={presetColor}
                    className={classes.pickerSwatch}
                    style={{ background: presetColor }}
                    onClick={(e) => {
                      handleChange(presetColor);
                      e.stopPropagation();
                    }}
                  />
                ))}
              </div>
            </div>,
          ],
          document.body
        )}
    </div>
  );
};

SketchColorPicker.propTypes = {
  color: PropTypes.string.isRequired,
  handleChange: PropTypes.func,
  onClose: PropTypes.func,
  includeWhite: PropTypes.bool,
  interactive: PropTypes.bool,
};

SketchColorPicker.defaultProps = {
  handleChange: () => {},
  onClose: () => {},
  includeWhite: false,
};

export default SketchColorPicker;

const useStyles = makeStyles({
  popover: {
    position: "relative",
  },
  pickerContainer: {
    position: "fixed",
    zIndex: "10000000",
    marginRight: "10px",
    padding: "16px",
    borderRadius: "5px",
    background: "#33333a",
    boxShadow: "0 6px 12px #999",
  },
  color: {
    width: "36px",
    height: "14px",
    borderRadius: "2px",
  },
  mainSwatch: {
    padding: "5px",
    background: "#fff",
    borderRadius: "1px",
    boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
    display: "inline-block",
    cursor: "pointer",
    marginTop: 6,
  },
  cover: {
    position: "fixed",
    top: "0px",
    right: "0px",
    bottom: "0px",
    left: "0px",
    zIndex: "1",
  },
  pickerSwatches: {
    display: "flex",
    flexWrap: "wrap",
    paddingTop: "12px",
    justifyContent: "center",
  },
  pickerSwatch: {
    width: "20px",
    aspectRatio: "1",
    margin: "4px",
    border: "none",
    padding: 0,
    borderRadius: "4px",
    cursor: "pointer",
    outline: "none",
  },
});
